import { cn } from '@/lib/utils'

/**
 * One fact tile (value, label, context), restored from Driver-Auto-CC
 * (`git show 2a4551d^:frontend/src/components/approver/StatTile.tsx`) and
 * generalised (plan 08 WP-VIS-06). A missing value shows a dash. `onSelect`
 * turns the value into a button (the buyer page uses it to switch the viewer
 * to a PO or GRN). Domain-free: no AP nouns in the props.
 */
export function StatTile({
  value,
  label,
  context,
  emphasis = false,
  compact = false,
  mono = false,
  onSelect,
  selectLabel,
  className,
}: {
  value?: string | number | null
  label: string
  context?: string | null
  /** Amber edge for a tile that needs the viewer; pass `context` to say why (not colour alone). */
  emphasis?: boolean
  /** Smaller value type, for a row of four inside a card. */
  compact?: boolean
  /** Monospace value (document numbers). */
  mono?: boolean
  onSelect?: () => void
  /** Accessible name for the button when `onSelect` is set. */
  selectLabel?: string
  className?: string
}) {
  const shown = value === null || value === undefined || value === '' ? '-' : String(value)
  const valueClass = cn(
    'font-display font-semibold text-foreground tabular-nums',
    compact ? 'text-base' : 'text-2xl',
    mono && 'font-mono text-sm'
  )
  return (
    <div
      className={cn(
        'rounded-xl border bg-card shadow-soft',
        compact ? 'p-3' : 'p-4',
        emphasis ? 'border-warning-strong/50 bg-warning-wash' : 'border-hairline',
        className
      )}
    >
      <p className='text-micro uppercase text-muted-foreground'>{label}</p>
      {onSelect && shown !== '-' ? (
        <button
          type='button'
          onClick={onSelect}
          aria-label={selectLabel}
          className={cn(
            valueClass,
            'mt-1 rounded text-left underline decoration-brand-cornflower decoration-2 underline-offset-4',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
          )}
        >
          {shown}
        </button>
      ) : (
        <p className={cn(valueClass, 'mt-1')}>{shown}</p>
      )}
      {context && <p className='mt-1 text-xs text-foreground'>{context}</p>}
    </div>
  )
}
